import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { transactionsAPI, statsAPI, withdrawalsAPI, configAPI, depositsAPI, authAPI, savingsAPI } from '../services/api';
import { QRCodeSVG } from 'qrcode.react';
import Layout from '../components/Layout';
import { usdt } from '../utils/currency';
import './Wallet.css';

// Bouton "Comment faire ?" en popover flottant: le guide se déplie
// au-dessus du contenu (ne pousse rien, contrairement à une liste inline)
// et se ferme au clic en dehors. Peut inclure un aperçu visuel replié
// derrière un second bouton pour ne rien afficher par défaut.
const MiniGuidePopover = ({ label, steps, preview, previewLabel, previewHideLabel, variant = 'pill' }) => {
  const [open, setOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  return (
    <div className={`mini-guide-wrap variant-${variant}`} ref={wrapRef}>
      <button type="button" className={`mini-guide-toggle ${variant}`} onClick={() => setOpen(v => !v)}>
        <span className="mini-guide-tap">👆</span>
        {variant !== 'icon' && label}
      </button>
      {open && (
        <div className="mini-guide-popover">
          <ol className="mini-guide-list">
            {steps.map((step, i) => <li key={i}>{step}</li>)}
          </ol>
          {preview && (
            <>
              <button
                type="button"
                className="mini-guide-preview-toggle"
                onClick={() => setShowPreview(v => !v)}
              >
                {showPreview ? previewHideLabel : previewLabel}
              </button>
              {showPreview && <div className="mini-guide-preview">{preview}</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const Wallet = () => {
  const { user, refreshUser } = useAuth();
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [chartPeriod, setChartPeriod] = useState('1M');
  const [tooltip, setTooltip] = useState(null);
  const [projectionPeriod, setProjectionPeriod] = useState('3M');
  const [projectionTooltip, setProjectionTooltip] = useState(null);
  const [stats, setStats] = useState({
    totalProfit: 0,
    totalTrades: 0,
    winRate: 0,
    bestTrade: 0
  });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  const [chartStats, setChartStats] = useState({ change: 0, changePercent: 0, min: 0, max: 0, firstValue: 0, lastValue: 0 });
  const [chartLoading, setChartLoading] = useState(true);
  const chartRef = useRef(null);
  const projectionChartRef = useRef(null);

  // États pour le dépôt
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositMethod, setDepositMethod] = useState('txid'); // 'txid' | 'code'
  const [depositToken, setDepositToken] = useState('');
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositError, setDepositError] = useState('');
  const [tokenCopied, setTokenCopied] = useState(false);
  const [tokenTimeLeft, setTokenTimeLeft] = useState(30);
  const depositTimerRef = useRef(null);

  // États pour le dépôt par virement réel (TXID, réseau TRC20)
  const [depositAddress, setDepositAddress] = useState('');
  const [addressCopied, setAddressCopied] = useState(false);
  const [txidInput, setTxidInput] = useState('');
  const [txidAmountInput, setTxidAmountInput] = useState('');
  const [txidSubmitting, setTxidSubmitting] = useState(false);
  const [txidMsg, setTxidMsg] = useState({ type: '', text: '' });
  const [depositHistory, setDepositHistory] = useState([]);

  // États pour les retraits
  const [withdrawalStatus, setWithdrawalStatus] = useState(null);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [skipMonths, setSkipMonths] = useState(1);
  const [withdrawalLoading, setWithdrawalLoading] = useState(false);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [withdrawalError, setWithdrawalError] = useState('');
  const [simTradesPerDay, setSimTradesPerDay] = useState(3);
  const [tradingConfig, setTradingConfig] = useState(null);

  // États adresse de retrait (TRC20)
  const [withdrawalAddressInput, setWithdrawalAddressInput] = useState('');
  // Portefeuilles épargne
  const [savingsWallets, setSavingsWallets] = useState([]);
  const [savingsSelectedId, setSavingsSelectedId] = useState(null);
  const [savingsModal, setSavingsModal] = useState(null); // wallet en cours de retrait
  const [savingsAmount, setSavingsAmount] = useState('');
  const [savingsSubmitting, setSavingsSubmitting] = useState(false);
  const [savingsMsg, setSavingsMsg] = useState({ type: '', text: '' });
  const [addressSubmitting, setAddressSubmitting] = useState(false);
  const [addressMsg, setAddressMsg] = useState({ type: '', text: '' });

  // Fetch stats and transactions on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, txRes] = await Promise.all([
          statsAPI.get(),
          transactionsAPI.getAll({ limit: 50 })
        ]);

        if (statsRes.data) {
          setStats({
            totalProfit: statsRes.data.totalProfit || 0,
            totalTrades: statsRes.data.totalTrades || 0,
            winRate: statsRes.data.winRate || 0,
            bestTrade: statsRes.data.bestTrade || 0
          });
        }

        if (txRes.data?.transactions) {
          // Historique portefeuille = mouvements de fonds uniquement (pas les trades)
          const FUND_TYPES = ['deposit', 'admin_credit', 'admin_debit', 'withdrawal'];
          setTransactions(txRes.data.transactions.filter(tx => FUND_TYPES.includes(tx.type)));
        }
      } catch (error) {
        console.error('Erreur chargement données wallet:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Charger la config trading depuis le serveur (trades/jour, gains moyens, etc.)
  // Charger les portefeuilles épargne au montage
  useEffect(() => {
    savingsAPI.getMine().then(r => {
      const ws = r.data?.wallets || [];
      setSavingsWallets(ws);
      setSavingsSelectedId(ws[0]?.id || null);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchTradingConfig = async () => {
      try {
        const response = await configAPI.getTradingSummary();
        if (response.data?.trading) {
          setTradingConfig(response.data.trading);
          // Initialiser avec le nombre réel de signaux/jour de l'utilisateur (base + codes actifs)
          setSimTradesPerDay(response.data.trading.maxSignals || response.data.trading.baseSignals || 3);
        }
      } catch (error) {
        console.error('Erreur chargement config trading:', error);
      }
    };
    fetchTradingConfig();
  }, []);

  // Charger le statut de retrait
  useEffect(() => {
    const fetchWithdrawalData = async () => {
      try {
        const [statusRes, historyRes] = await Promise.all([
          withdrawalsAPI.getStatus(),
          withdrawalsAPI.getHistory()
        ]);

        if (statusRes.data) {
          setWithdrawalStatus(statusRes.data);
        }
        if (historyRes.data?.withdrawals) {
          setWithdrawalHistory(historyRes.data.withdrawals);
        }
      } catch (error) {
        console.error('Erreur chargement données retrait:', error);
      }
    };

    fetchWithdrawalData();
  }, []);

  // Demander un retrait
  const handleWithdrawalRequest = async () => {
    if (!withdrawalAmount || parseAmount(withdrawalAmount) <= 0) {
      setWithdrawalError(t('wallet.invalidAmount'));
      return;
    }

    setWithdrawalLoading(true);
    setWithdrawalError('');

    try {
      const response = await withdrawalsAPI.request(parseAmount(withdrawalAmount));
      if (response.data) {
        // Rafraîchir tout : user global + statut retrait + historique
        await Promise.all([
          refreshUser(),
          withdrawalsAPI.getStatus().then(r => setWithdrawalStatus(r.data)),
          withdrawalsAPI.getHistory().then(r => setWithdrawalHistory(r.data?.withdrawals || []))
        ]);
        setWithdrawalAmount('');
      }
    } catch (error) {
      setWithdrawalError(error.response?.data?.error || t('wallet.withdrawalError'));
    } finally {
      setWithdrawalLoading(false);
    }
  };

  // Soumettre son adresse de retrait TRC20 (vérifiée en temps réel côté serveur:
  // format + unicité, puis validation manuelle par l'admin)
  const handleWithdrawalAddressSubmit = async (e) => {
    e.preventDefault();
    setAddressSubmitting(true);
    setAddressMsg({ type: '', text: '' });
    try {
      const res = await authAPI.submitWithdrawalAddress(withdrawalAddressInput.trim());
      setAddressMsg({ type: 'success', text: res.data?.message || t('wallet.addressSaved') });
      setWithdrawalAddressInput('');
      // Rafraîchir le statut de retrait (contient l'état de l'adresse)
      const statusRes = await withdrawalsAPI.getStatus();
      setWithdrawalStatus(statusRes.data);
    } catch (err) {
      setAddressMsg({ type: 'error', text: err.response?.data?.error || t('wallet.addressSubmitError') });
    } finally {
      setAddressSubmitting(false);
    }
  };

  // Sauter des mois de retrait
  const handleSkipMonths = async () => {
    setWithdrawalLoading(true);
    setWithdrawalError('');

    try {
      await withdrawalsAPI.skip(skipMonths);
      // Rafraîchir user global (extraSuggestionsPerDay mis à jour) + statut retrait
      await Promise.all([
        refreshUser(),
        withdrawalsAPI.getStatus().then(r => setWithdrawalStatus(r.data))
      ]);
      setShowSkipModal(false);
      setSkipMonths(1);
    } catch (error) {
      setWithdrawalError(error.response?.data?.error || t('wallet.skipError'));
    } finally {
      setWithdrawalLoading(false);
    }
  };

  // Countdown quand un token est généré — efface le token après 30s
  useEffect(() => {
    clearInterval(depositTimerRef.current);
    if (!depositToken) { setTokenTimeLeft(30); return; }

    setTokenTimeLeft(30);
    depositTimerRef.current = setInterval(() => {
      setTokenTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(depositTimerRef.current);
          setDepositToken('');
          setTokenCopied(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(depositTimerRef.current);
  }, [depositToken]);

  // Générer un token de dépôt
  const handleGenerateDepositToken = async () => {
    setDepositLoading(true);
    setDepositError('');
    setDepositToken('');
    setTokenCopied(false);
    try {
      const response = await depositsAPI.generate();
      if (response.data?.token) {
        setDepositToken(response.data.token);
      }
    } catch (error) {
      setDepositError(error.response?.data?.error || t('wallet.depositCodeError'));
    } finally {
      setDepositLoading(false);
    }
  };

  const handleCopyToken = () => {
    if (depositToken) {
      navigator.clipboard.writeText(depositToken).then(() => {
        setTokenCopied(true);
        setTimeout(() => setTokenCopied(false), 2500);
      });
    }
  };

  const handleCopyAddress = () => {
    if (depositAddress) {
      navigator.clipboard.writeText(depositAddress).then(() => {
        setAddressCopied(true);
        setTimeout(() => setAddressCopied(false), 2500);
      });
    }
  };

  // Bloquer le scroll de la page derrière le modal (technique position:fixed —
  // la seule fiable sur iOS Safari, cf. Markets.js). Sans ça le scroll passe
  // "à travers" le modal et défile la page au lieu de son contenu.
  useEffect(() => {
    if (showDepositModal) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showDepositModal]);

  // Fermer le modal de dépôt avec Échap (desktop)
  useEffect(() => {
    if (!showDepositModal) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowDepositModal(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showDepositModal]);

  // Charger l'adresse de dépôt TRC20 + l'historique dès l'ouverture du modal
  useEffect(() => {
    if (!showDepositModal) return;
    depositsAPI.getAddress().then(res => {
      setDepositAddress(res.data?.address || '');
    }).catch(() => {});
    depositsAPI.getHistory().then(res => {
      setDepositHistory(res.data?.deposits || []);
    }).catch(() => {});
  }, [showDepositModal]);

  // Normalise un montant saisi (accepte "100.50" et "100,50") → nombre
  const parseAmount = (v) => parseFloat(String(v ?? '').replace(',', '.'));

  // ---- Portefeuilles épargne ----
  const loadSavings = () => {
    savingsAPI.getMine().then(r => {
      const ws = r.data?.wallets || [];
      setSavingsWallets(ws);
      setSavingsSelectedId(prev => (prev && ws.some(w => w.id === prev)) ? prev : (ws[0]?.id || null));
    }).catch(() => {});
  };

  const handleSavingsWithdraw = async () => {
    const amt = parseAmount(savingsAmount);
    if (!(amt > 0)) { setSavingsMsg({ type: 'error', text: 'Montant invalide' }); return; }
    if (savingsModal && amt > savingsModal.balance) { setSavingsMsg({ type: 'error', text: 'Montant supérieur au solde du portefeuille' }); return; }
    setSavingsSubmitting(true); setSavingsMsg({ type: '', text: '' });
    try {
      const res = await savingsAPI.withdraw(savingsModal.id, amt);
      setSavingsMsg({ type: 'success', text: res.data?.message || 'Demande envoyée' });
      setSavingsAmount('');
      loadSavings();
      setTimeout(() => { setSavingsModal(null); setSavingsMsg({ type: '', text: '' }); }, 2000);
    } catch (err) {
      setSavingsMsg({ type: 'error', text: err.response?.data?.error || 'Erreur lors de la demande de retrait' });
    } finally {
      setSavingsSubmitting(false);
    }
  };

  // Soumettre un TXID de virement réel (réseau TRC20)
  const handleSubmitTxid = async (e) => {
    e.preventDefault();
    setTxidSubmitting(true);
    setTxidMsg({ type: '', text: '' });
    try {
      const res = await depositsAPI.submitTxid(txidInput.trim(), parseAmount(txidAmountInput));
      setTxidMsg({ type: 'success', text: res.data?.message || t('wallet.txidSubmitSuccess') });
      setTxidInput('');
      setTxidAmountInput('');
      const histRes = await depositsAPI.getHistory();
      setDepositHistory(histRes.data?.deposits || []);
    } catch (err) {
      setTxidMsg({ type: 'error', text: err.response?.data?.error || t('wallet.txidSubmitError') });
    } finally {
      setTxidSubmitting(false);
    }
  };

  // Annuler une demande de retrait
  const handleCancelWithdrawal = async (id) => {
    try {
      await withdrawalsAPI.cancel(id);
      // Rafraîchir user global + statut + historique
      await Promise.all([
        refreshUser(),
        withdrawalsAPI.getStatus().then(r => setWithdrawalStatus(r.data)),
        withdrawalsAPI.getHistory().then(r => setWithdrawalHistory(r.data?.withdrawals || []))
      ]);
    } catch (error) {
      setWithdrawalError(error.response?.data?.error || t('wallet.cancelError'));
    }
  };

  // Fetch chart history data when period changes
  useEffect(() => {
    const fetchChartHistory = async () => {
      setChartLoading(true);
      try {
        const response = await statsAPI.getHistory(chartPeriod);
        if (response.data) {
          const history = response.data.history || [];
          // Format data for chart
          const formattedData = history.map(point => ({
            date: new Date(point.date),
            value: point.value,
            label: formatChartLabel(new Date(point.date), chartPeriod)
          }));
          setChartData(formattedData);
          setChartStats({
            change: response.data.change || 0,
            changePercent: response.data.changePercent || 0,
            min: formattedData.length > 0 ? Math.min(...formattedData.map(d => d.value)) : 0,
            max: formattedData.length > 0 ? Math.max(...formattedData.map(d => d.value)) : 0,
            firstValue: formattedData.length > 0 ? formattedData[0].value : 0,
            lastValue: formattedData.length > 0 ? formattedData[formattedData.length - 1].value : 0
          });
        }
      } catch (error) {
        console.error('Erreur chargement historique:', error);
        // Fallback to current balance if no history
        const currentBalance = user?.balance || 0;
        setChartData([
          { date: new Date(), value: currentBalance, label: t('wallet.now') }
        ]);
        setChartStats({ change: 0, changePercent: 0, min: currentBalance, max: currentBalance, firstValue: currentBalance, lastValue: currentBalance });
      } finally {
        setChartLoading(false);
      }
    };

    fetchChartHistory();
  }, [chartPeriod, user?.balance]);

  // Format chart label based on period
  const formatChartLabel = (date, period) => {
    const locale = language === 'en' ? 'en-US' : 'fr-FR';
    switch (period) {
      case '1J':
        return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
      case '1S':
        return date.toLocaleDateString(locale, { weekday: 'short' });
      case '1M':
      case '3M':
        return date.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
      case '1A':
      case 'MAX':
        return date.toLocaleDateString(locale, { month: 'short', year: '2-digit' });
      default:
        return date.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
    }
  };

  const formatCurrency = (value) => usdt(value);

  // Générer le path SVG pour la courbe
  const generateChartPath = useCallback(() => {
    if (chartData.length === 0) return { linePath: '', areaPath: '' };

    const width = 320;
    const height = 120;
    const padding = { top: 10, bottom: 10, left: 0, right: 0 };

    const values = chartData.map(d => d.value);
    const minVal = Math.min(...values) * 0.998;
    const maxVal = Math.max(...values) * 1.002;
    const range = maxVal - minVal || 1;

    const xStep = (width - padding.left - padding.right) / (chartData.length - 1);

    const points = chartData.map((d, i) => {
      const x = padding.left + i * xStep;
      const y = height - padding.bottom - ((d.value - minVal) / range) * (height - padding.top - padding.bottom);
      return { x, y, ...d };
    });

    // Créer une courbe lisse avec des courbes de Bézier
    let linePath = `M ${points[0].x} ${points[0].y}`;

    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const cpx = (prev.x + curr.x) / 2;
      linePath += ` C ${cpx} ${prev.y}, ${cpx} ${curr.y}, ${curr.x} ${curr.y}`;
    }

    // Chemin pour l'aire sous la courbe
    const areaPath = linePath + ` L ${points[points.length - 1].x} ${height} L ${points[0].x} ${height} Z`;

    return { linePath, areaPath, points };
  }, [chartData]);

  const { linePath, areaPath, points } = useMemo(() => generateChartPath(), [generateChartPath]);

  // Gérer l'interaction tactile/souris sur le graphique
  const handleChartInteraction = useCallback((e) => {
    if (!chartRef.current || !points || points.length === 0) return;

    const rect = chartRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    const relativeX = (x / rect.width) * 320;

    // Trouver le point le plus proche
    let closestPoint = points[0];
    let closestDistance = Math.abs(points[0].x - relativeX);
    let closestIndex = 0;

    points.forEach((point, index) => {
      const distance = Math.abs(point.x - relativeX);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPoint = point;
        closestIndex = index;
      }
    });

    setTooltip({
      x: closestPoint.x,
      y: closestPoint.y,
      value: closestPoint.value,
      label: closestPoint.label,
      date: closestPoint.date,
      index: closestIndex
    });
  }, [points]);

  const handleChartLeave = useCallback(() => {
    setTooltip(null);
  }, []);

  // Générer les données de prévision basées sur le modèle RÉEL du produit:
  // le multiplicateur mensuel (ex: x2 bronze) est atteint avec normalSlots
  // trades/jour; chaque trade au-delà vaut bonusFactor (1/3) d'un trade normal.
  const generateProjectionData = useCallback((period) => {
    const baseValue = parseFloat(user?.balance) || 0;
    const now = new Date();
    let points = [];
    let months;

    // Taux affiché et volatilité de la courbe
    const winRate = tradingConfig?.successRate || stats.winRate || 50;

    // Modèle actuel (depuis la config serveur):
    // - les signaux de BASE (baseSignals) doublent le solde sur un cycle (baseMultiplier)
    // - chaque signal bonus supplémentaire ajoute bonusCodeFactor sur le cycle
    const baseMultiplier = tradingConfig?.baseMultiplier || 2.0;
    const bonusCodeFactor = tradingConfig?.bonusCodeFactor || 1.06;
    const baseSignals = tradingConfig?.baseSignals || 3;
    const cycleDays = tradingConfig?.cycleLengthDays || 30;

    // Nb de signaux bonus simulés = signaux au-delà de la base
    const simBonus = Math.max(0, simTradesPerDay - baseSignals);
    // Multiplicateur effectif sur un cycle, puis taux journalier équivalent
    const monthlyMultiplier = baseMultiplier * Math.pow(bonusCodeFactor, simBonus);
    const dailyGrowthRate = Math.pow(monthlyMultiplier, 1 / cycleDays) - 1;

    switch (period) {
      case '1M':
        months = 1;
        break;
      case '3M':
        months = 3;
        break;
      case '6M':
        months = 6;
        break;
      default:
        months = 3;
    }

    const totalDays = months * 30;

    // Point de départ (aujourd'hui) - solde réel
    points.push({
      date: now,
      value: baseValue,
      label: t('wallet.today'),
      isToday: true
    });

    // Générer les points futurs avec une légère variation réaliste
    for (let i = 1; i <= totalDays; i += Math.ceil(totalDays / 20)) {
      const futureDate = new Date(now.getTime() + i * 24 * 60 * 60 * 1000);
      const baseGrowth = Math.pow(1 + dailyGrowthRate, i);
      // Variation basée sur le taux de succès (plus stable si winRate élevé)
      const volatility = Math.max(0.005, (100 - winRate) / 5000);
      const variation = 1 + (Math.random() - 0.5) * volatility;
      const projectedValue = baseValue * baseGrowth * variation;

      const locale = language === 'en' ? 'en-US' : 'fr-FR';
      points.push({
        date: futureDate,
        value: Math.max(0, projectedValue),
        label: futureDate.toLocaleDateString(locale, { day: '2-digit', month: 'short' }),
        isToday: false
      });
    }

    return points;
  }, [user?.balance, stats.winRate, simTradesPerDay, tradingConfig, t, language]);

  const projectionData = useMemo(() => generateProjectionData(projectionPeriod), [projectionPeriod, generateProjectionData]);

  // Calculer les statistiques de projection
  const projectionStats = useMemo(() => {
    if (projectionData.length < 2) return { projectedValue: 0, gain: 0, gainPercent: 0 };
    const startValue = projectionData[0].value;
    const endValue = projectionData[projectionData.length - 1].value;
    const gain = endValue - startValue;
    // Garde division par zéro: solde 0 (nouveau compte) → 0% et non NaN%
    const gainPercent = startValue > 0 ? ((endValue - startValue) / startValue) * 100 : 0;
    return {
      projectedValue: endValue,
      gain,
      gainPercent,
      startValue
    };
  }, [projectionData]);

  // Générer le path SVG pour la courbe de projection
  const generateProjectionPath = useCallback(() => {
    if (projectionData.length === 0) return { linePath: '', areaPath: '', points: [] };

    const width = 320;
    const height = 120;
    const padding = { top: 10, bottom: 10, left: 0, right: 0 };

    const values = projectionData.map(d => d.value);
    const minVal = Math.min(...values) * 0.995;
    const maxVal = Math.max(...values) * 1.005;
    const range = maxVal - minVal || 1;

    const xStep = (width - padding.left - padding.right) / (projectionData.length - 1);

    const projPoints = projectionData.map((d, i) => {
      const x = padding.left + i * xStep;
      const y = height - padding.bottom - ((d.value - minVal) / range) * (height - padding.top - padding.bottom);
      return { x, y, ...d };
    });

    let linePath = `M ${projPoints[0].x} ${projPoints[0].y}`;

    for (let i = 1; i < projPoints.length; i++) {
      const prev = projPoints[i - 1];
      const curr = projPoints[i];
      const cpx = (prev.x + curr.x) / 2;
      linePath += ` C ${cpx} ${prev.y}, ${cpx} ${curr.y}, ${curr.x} ${curr.y}`;
    }

    const areaPath = linePath + ` L ${projPoints[projPoints.length - 1].x} ${height} L ${projPoints[0].x} ${height} Z`;

    return { linePath, areaPath, points: projPoints };
  }, [projectionData]);

  const { linePath: projLinePath, areaPath: projAreaPath, points: projPoints } = useMemo(() => generateProjectionPath(), [generateProjectionPath]);

  // Gérer l'interaction sur le graphique de projection
  const handleProjectionInteraction = useCallback((e) => {
    if (!projectionChartRef.current || !projPoints || projPoints.length === 0) return;

    const rect = projectionChartRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    const relativeX = (x / rect.width) * 320;

    let closestPoint = projPoints[0];
    let closestDistance = Math.abs(projPoints[0].x - relativeX);

    projPoints.forEach((point) => {
      const distance = Math.abs(point.x - relativeX);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPoint = point;
      }
    });

    setProjectionTooltip({
      x: closestPoint.x,
      y: closestPoint.y,
      value: closestPoint.value,
      label: closestPoint.label,
      isToday: closestPoint.isToday
    });
  }, [projPoints]);

  const handleProjectionLeave = useCallback(() => {
    setProjectionTooltip(null);
  }, []);

  return (
    <Layout>
      <div className="wallet-page">
        {/* Header */}
        <div className="wallet-header">
          <h1>{t('wallet.title')}</h1>
        </div>

        {/* Balance Card */}
        <div className="balance-section">
          <div className="balance-top">
            <span className="balance-label">{t('wallet.totalBalance')}</span>
            <span className="balance-currency">USD</span>
          </div>

          <div className="balance-amount-wrapper">
            <span className="balance-amount">{formatCurrency(user?.balance)}</span>
            <div className={`balance-change ${stats.totalProfit >= 0 ? 'positive' : 'negative'}`}>
              {/* parseFloat obligatoire: balance arrive en CHAÎNE ("0.00" est truthy → NaN) */}
              <span>{stats.totalProfit >= 0 ? '+' : ''}{((stats.totalProfit / (parseFloat(user?.balance) || 1)) * 100).toFixed(2)}%</span>
            </div>
          </div>

          <div className="balance-stats-row">
            <div className="balance-stat-item">
              <span className="stat-dot green"></span>
              <span className="stat-text">{t('wallet.profit')}</span>
              <span className={`stat-number ${stats.totalProfit >= 0 ? 'green' : 'red'}`}>
                {stats.totalProfit >= 0 ? '+' : ''}{formatCurrency(stats.totalProfit)}
              </span>
            </div>
            <div className="balance-stat-item">
              <span className="stat-dot yellow"></span>
              <span className="stat-text">{t('wallet.trades')}</span>
              <span className="stat-number">{stats.totalTrades}</span>
            </div>
          </div>

          {/* Bouton Recharger */}
          <button className="deposit-btn" onClick={() => { setShowDepositModal(true); setDepositToken(''); setDepositError(''); }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 5v14M5 12l7-7 7 7"/>
            </svg>
            Recharger mon compte
          </button>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-card-icon green">📈</div>
            <div className="stat-card-content">
              <span className="stat-card-value">{stats.winRate}%</span>
              <span className="stat-card-label">{t('wallet.successRate')}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon yellow">🏆</div>
            <div className="stat-card-content">
              <span className="stat-card-value">{formatCurrency(stats.bestTrade)}</span>
              <span className="stat-card-label">{t('wallet.bestTrade')}</span>
            </div>
          </div>
        </div>

        {/* Portefeuilles épargne (séparés du trading) */}
        {savingsWallets.length > 0 && (() => {
          const w = savingsWallets.find(x => x.id === savingsSelectedId) || savingsWallets[0];
          if (!w) return null;
          return (
            <div className="savings-section" style={{
              margin: '18px 0 4px', border: '1px solid rgba(45,212,191,0.35)', borderRadius: 14,
              background: 'linear-gradient(180deg, rgba(45,212,191,0.06), rgba(45,212,191,0.02))', padding: 16
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>💰 Portefeuille épargne</span>
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: 'rgba(45,212,191,0.15)', color: '#2dd4bf', fontWeight: 600 }}>
                  hors trading
                </span>
              </div>

              {savingsWallets.length > 1 && (
                <select
                  value={savingsSelectedId || ''}
                  onChange={(e) => setSavingsSelectedId(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 10, marginBottom: 12, fontSize: 14,
                    border: '1px solid var(--border-color,#2a2f36)', background: 'var(--bg-elevated,#1a1f26)', color: 'inherit' }}
                >
                  {savingsWallets.map(x => (
                    <option key={x.id} value={x.id}>{x.name} — {Number(x.balance).toFixed(2)} USDT</option>
                  ))}
                </select>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 13, opacity: 0.7 }}>{w.name}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, margin: '2px 0', color: '#2dd4bf' }}>{formatCurrency(w.balance)}</div>
                  <div style={{ fontSize: 12, opacity: 0.65 }}>
                    {w.role === 'owner' ? 'Vous êtes propriétaire' : 'Vous êtes associé'}
                    {w.coHolder ? ` · avec ${w.coHolder}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => { setSavingsModal(w); setSavingsAmount(''); setSavingsMsg({ type: '', text: '' }); }}
                  disabled={!(w.balance > 0)}
                  style={{ padding: '10px 20px', borderRadius: 10, cursor: w.balance > 0 ? 'pointer' : 'not-allowed',
                    border: 'none', background: '#2dd4bf', color: '#04211d', fontWeight: 700, fontSize: 14, opacity: w.balance > 0 ? 1 : 0.5 }}
                >
                  Retirer
                </button>
              </div>
            </div>
          );
        })()}

        {/* Tabs */}
        <div className="wallet-tabs">
          <button
            className={`wallet-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
            {t('wallet.overview')}
          </button>
          <button
            className={`wallet-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('history');
              // Notifier que l'historique a été consulté (pour reset le badge wallet)
              window.dispatchEvent(new CustomEvent('wallet-history-viewed'));
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            {t('wallet.history')}
          </button>
          <button
            className={`wallet-tab ${activeTab === 'withdrawal' ? 'active' : ''}`}
            onClick={() => setActiveTab('withdrawal')}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/>
              <path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/>
            </svg>
            {t('wallet.withdrawal')}
          </button>
        </div>

        {/* Content */}
        <div className="wallet-content">
          {activeTab === 'overview' ? (
            <div className="overview-section">
              {/* Assets */}
              <div className="account-card">
                <h3>{t('wallet.myAssets')}</h3>
                <div className="assets-list">
                  <div className="asset-item">
                    <div className="asset-icon usd">₮</div>
                    <div className="asset-info">
                      <span className="asset-name">USDT</span>
                      <span className="asset-full">{t('wallet.usDollar')}</span>
                    </div>
                    <div className="asset-values">
                      <span className="asset-amount">{formatCurrency(user?.balance)}</span>
                      <span className="asset-percent">100%</span>
                    </div>
                  </div>
                  <div className="asset-item empty">
                    <div className="asset-icon btc">₿</div>
                    <div className="asset-info">
                      <span className="asset-name">BTC</span>
                      <span className="asset-full">Bitcoin</span>
                    </div>
                    <div className="asset-values">
                      <span className="asset-amount">0.00</span>
                      <span className="asset-percent">0%</span>
                    </div>
                  </div>
                </div>
                <p className="assets-hint">{t('wallet.cryptoHint')}</p>
              </div>

              {/* Performance Chart Interactive */}
              <div className="performance-card">
                <div className="performance-header">
                  <h3>{t('wallet.performance')}</h3>
                  <div className={`performance-change ${chartStats.changePercent >= 0 ? 'positive' : 'negative'}`}>
                    {chartStats.changePercent >= 0 ? '+' : ''}{chartStats.changePercent.toFixed(2)}%
                  </div>
                </div>

                {/* Period Tabs */}
                <div className="chart-periods">
                  {['1J', '1S', '1M', '3M', '1A', 'MAX'].map((period) => (
                    <button
                      key={period}
                      className={`period-btn ${chartPeriod === period ? 'active' : ''}`}
                      onClick={() => setChartPeriod(period)}
                    >
                      {period}
                    </button>
                  ))}
                </div>

                {/* Interactive Chart */}
                <div
                  className="chart-container"
                  ref={chartRef}
                  onMouseMove={handleChartInteraction}
                  onMouseLeave={handleChartLeave}
                  onTouchMove={handleChartInteraction}
                  onTouchEnd={handleChartLeave}
                >
                  <svg viewBox="0 0 320 120" className="interactive-chart" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="performanceGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={chartStats.changePercent >= 0 ? "#0ecb81" : "#f6465d"} stopOpacity="0.3"/>
                        <stop offset="100%" stopColor={chartStats.changePercent >= 0 ? "#0ecb81" : "#f6465d"} stopOpacity="0"/>
                      </linearGradient>
                      <filter id="glow">
                        <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                        <feMerge>
                          <feMergeNode in="coloredBlur"/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                    </defs>

                    {/* Area fill */}
                    <path d={areaPath} fill="url(#performanceGradient)" />

                    {/* Line */}
                    <path
                      d={linePath}
                      fill="none"
                      stroke={chartStats.changePercent >= 0 ? "#0ecb81" : "#f6465d"}
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      filter="url(#glow)"
                    />

                    {/* Tooltip indicator */}
                    {tooltip && (
                      <>
                        {/* Vertical line */}
                        <line
                          x1={tooltip.x}
                          y1="0"
                          x2={tooltip.x}
                          y2="120"
                          stroke="#848e9c"
                          strokeWidth="1"
                          strokeDasharray="4,4"
                          opacity="0.5"
                        />
                        {/* Point indicator */}
                        <circle
                          cx={tooltip.x}
                          cy={tooltip.y}
                          r="6"
                          fill={chartStats.changePercent >= 0 ? "#0ecb81" : "#f6465d"}
                          stroke="#0b0e11"
                          strokeWidth="2"
                        />
                        <circle
                          cx={tooltip.x}
                          cy={tooltip.y}
                          r="10"
                          fill={chartStats.changePercent >= 0 ? "#0ecb81" : "#f6465d"}
                          opacity="0.3"
                        />
                      </>
                    )}
                  </svg>

                  {/* Tooltip box */}
                  {tooltip && (
                    <div
                      className="chart-tooltip"
                      style={{
                        left: `${Math.min(Math.max(tooltip.x / 320 * 100, 15), 85)}%`
                      }}
                    >
                      <span className="tooltip-value">{formatCurrency(tooltip.value)}</span>
                      <span className="tooltip-date">{tooltip.label}</span>
                    </div>
                  )}
                </div>

                {/* Chart Stats */}
                <div className="chart-stats">
                  <div className="chart-stat-item">
                    <span className="chart-stat-label">{t('wallet.min')}</span>
                    <span className="chart-stat-value">{formatCurrency(chartStats.min)}</span>
                  </div>
                  <div className="chart-stat-item">
                    <span className="chart-stat-label">{t('wallet.max')}</span>
                    <span className="chart-stat-value">{formatCurrency(chartStats.max)}</span>
                  </div>
                  <div className="chart-stat-item">
                    <span className="chart-stat-label">{t('wallet.variation')}</span>
                    <span className={`chart-stat-value ${chartStats.change >= 0 ? 'green' : 'red'}`}>
                      {chartStats.change >= 0 ? '+' : ''}{formatCurrency(chartStats.change)}
                    </span>
                  </div>
                </div>

                <p className="chart-hint">{t('wallet.chartHint')}</p>
              </div>

              {/* Projection Chart */}
              <div className="projection-card">
                <div className="projection-header">
                  <div className="projection-title-row">
                    <span className="projection-icon">🔮</span>
                    <h3>{t('wallet.growthForecast')}</h3>
                  </div>
                  <div className="projection-badge">
                    +{projectionStats.gainPercent.toFixed(1)}%
                  </div>
                </div>

                <p className="projection-subtitle">
                  {t('wallet.noWithdrawalEstimate')}
                </p>

                {/* Simulation - Trades par jour */}
                <div className="sim-controls">
                  <span className="sim-controls-label">
                    <span className="sim-controls-icon">⚡</span>
                    {t('wallet.tradesPerDayLabel')}
                  </span>
                  <div className="sim-trades-buttons">
                    {[1, 2, 3, 5, 7, 10].map((n) => (
                      <button
                        key={n}
                        className={`sim-trade-btn ${simTradesPerDay === n ? 'active' : ''}`}
                        onClick={() => setSimTradesPerDay(n)}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Projection Period Tabs */}
                <div className="projection-periods">
                  {['1M', '3M', '6M'].map((period) => (
                    <button
                      key={period}
                      className={`projection-period-btn ${projectionPeriod === period ? 'active' : ''}`}
                      onClick={() => setProjectionPeriod(period)}
                    >
                      {period === '1M' ? t('wallet.month1') : period === '3M' ? t('wallet.month3') : t('wallet.month6')}
                    </button>
                  ))}
                </div>

                {/* Projection Chart */}
                <div
                  className="projection-chart-container"
                  ref={projectionChartRef}
                  onMouseMove={handleProjectionInteraction}
                  onMouseLeave={handleProjectionLeave}
                  onTouchMove={handleProjectionInteraction}
                  onTouchEnd={handleProjectionLeave}
                >
                  <svg viewBox="0 0 320 120" className="projection-chart-svg" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="projectionGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f0b90b" stopOpacity="0.3"/>
                        <stop offset="100%" stopColor="#f0b90b" stopOpacity="0"/>
                      </linearGradient>
                      <linearGradient id="projectionLineGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#0ecb81"/>
                        <stop offset="100%" stopColor="#f0b90b"/>
                      </linearGradient>
                      <filter id="projectionGlow">
                        <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                        <feMerge>
                          <feMergeNode in="coloredBlur"/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                    </defs>

                    {/* Grid lines */}
                    <line x1="0" y1="30" x2="320" y2="30" stroke="#2b3139" strokeWidth="1" strokeDasharray="4,4" opacity="0.3"/>
                    <line x1="0" y1="60" x2="320" y2="60" stroke="#2b3139" strokeWidth="1" strokeDasharray="4,4" opacity="0.3"/>
                    <line x1="0" y1="90" x2="320" y2="90" stroke="#2b3139" strokeWidth="1" strokeDasharray="4,4" opacity="0.3"/>

                    {/* Area fill */}
                    <path d={projAreaPath} fill="url(#projectionGradient)" />

                    {/* Line */}
                    <path
                      d={projLinePath}
                      fill="none"
                      stroke="url(#projectionLineGradient)"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      filter="url(#projectionGlow)"
                    />

                    {/* Start point (today) */}
                    {projPoints.length > 0 && (
                      <circle
                        cx={projPoints[0].x}
                        cy={projPoints[0].y}
                        r="5"
                        fill="#0ecb81"
                        stroke="#0b0e11"
                        strokeWidth="2"
                      />
                    )}

                    {/* End point (projected) */}
                    {projPoints.length > 0 && (
                      <>
                        <circle
                          cx={projPoints[projPoints.length - 1].x}
                          cy={projPoints[projPoints.length - 1].y}
                          r="8"
                          fill="#f0b90b"
                          opacity="0.3"
                        />
                        <circle
                          cx={projPoints[projPoints.length - 1].x}
                          cy={projPoints[projPoints.length - 1].y}
                          r="5"
                          fill="#f0b90b"
                          stroke="#0b0e11"
                          strokeWidth="2"
                        />
                      </>
                    )}

                    {/* Tooltip indicator */}
                    {projectionTooltip && (
                      <>
                        <line
                          x1={projectionTooltip.x}
                          y1="0"
                          x2={projectionTooltip.x}
                          y2="120"
                          stroke="#f0b90b"
                          strokeWidth="1"
                          strokeDasharray="4,4"
                          opacity="0.5"
                        />
                        <circle
                          cx={projectionTooltip.x}
                          cy={projectionTooltip.y}
                          r="7"
                          fill={projectionTooltip.isToday ? "#0ecb81" : "#f0b90b"}
                          stroke="#0b0e11"
                          strokeWidth="2"
                        />
                      </>
                    )}
                  </svg>

                  {/* Tooltip box */}
                  {projectionTooltip && (
                    <div
                      className="projection-tooltip"
                      style={{
                        left: `${Math.min(Math.max(projectionTooltip.x / 320 * 100, 15), 85)}%`
                      }}
                    >
                      <span className="projection-tooltip-value">{formatCurrency(projectionTooltip.value)}</span>
                      <span className="projection-tooltip-date">{projectionTooltip.label}</span>
                    </div>
                  )}
                </div>

                {/* Projection Stats */}
                <div className="projection-stats">
                  <div className="projection-stat-item">
                    <span className="projection-stat-icon">💵</span>
                    <div className="projection-stat-content">
                      <span className="projection-stat-label">{t('wallet.currentBalance')}</span>
                      <span className="projection-stat-value">{formatCurrency(projectionStats.startValue)}</span>
                    </div>
                  </div>
                  <div className="projection-stat-arrow">→</div>
                  <div className="projection-stat-item highlight">
                    <span className="projection-stat-icon">🎯</span>
                    <div className="projection-stat-content">
                      <span className="projection-stat-label">{t('wallet.target')}</span>
                      <span className="projection-stat-value gold">{formatCurrency(projectionStats.projectedValue)}</span>
                    </div>
                  </div>
                </div>

                <div className="projection-gain-row">
                  <span className="projection-gain-label">{t('wallet.estimatedGain')}</span>
                  <span className="projection-gain-value">+{formatCurrency(projectionStats.gain)}</span>
                </div>

                {/* Stats utilisées pour la simulation */}
                <div className="projection-basis">
                  <span className="basis-label">{t('wallet.basedOn')}:</span>
                  <span className="basis-stat">📊 {tradingConfig?.successRate ?? stats.winRate ?? 0}% {t('wallet.successRate')}</span>
                  <span className="basis-stat">💰 {formatCurrency(user?.balance || 0)} {t('wallet.currentBalance')}</span>
                  <span className="basis-stat sim-basis-stat">⚡ {simTradesPerDay} {t('wallet.tradesPerDay')}</span>
                </div>

                {/* Disclaimer */}
                <div className="projection-disclaimer">
                  <span className="disclaimer-icon">ℹ️</span>
                  <p>{t('wallet.projectionDisclaimer')}</p>
                </div>
              </div>
            </div>
          ) : activeTab === 'history' ? (
            <div className="history-section">
              {transactions.length > 0 ? (
                <div className="transactions-list">
                  {transactions.map((tx) => (
                    <div key={tx.id} className={`transaction-item ${tx.amount >= 0 ? 'positive' : 'negative'}`}>
                      <div className="transaction-icon">
                        {tx.type === 'signup_bonus' && '🎁'}
                        {tx.type === 'referral_bonus' && '👥'}
                        {tx.type === 'trade_win' && '📈'}
                        {tx.type === 'trade_loss' && '📉'}
                        {tx.type === 'deposit' && '💵'}
                        {tx.type === 'withdrawal' && '💸'}
                        {tx.type === 'admin_credit' && '💰'}
                        {tx.type === 'admin_debit' && '💳'}
                      </div>
                      <div className="transaction-info">
                        <span className="transaction-description">{tx.description || tx.label || tx.type}</span>
                        <span className="transaction-date">
                          {(tx.date || tx.createdAt) ? new Date(tx.date || tx.createdAt).toLocaleDateString(language === 'en' ? 'en-US' : 'fr-FR', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '—'}
                        </span>
                      </div>
                      <div className="transaction-amount">
                        <span className={tx.amount >= 0 ? 'positive' : 'negative'}>
                          {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                        </span>
                        <span className="transaction-balance">
                          {t('wallet.balance')}: {formatCurrency(tx.balanceAfter)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-history">
                  <div className="empty-icon">📋</div>
                  <h3>{t('wallet.noTransactions')}</h3>
                  <p>{t('wallet.noTransactionsDesc')}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="withdrawal-section">
              {/* ── Adresse de retrait TRC20 (OBLIGATOIRE avant tout retrait) ── */}
              {withdrawalStatus && (() => {
                const wa = withdrawalStatus.withdrawalAddress || { status: 'none' };
                return (
                  <div className={`binance-card ${wa.status}`}>
                    <div className="binance-card-header">
                      <span className="binance-logo">◆</span>
                      <div className="binance-card-titles">
                        <h3>{t('wallet.addressTitle')}</h3>
                        <p>{t('wallet.addressSubtitle')}</p>
                      </div>
                      <span className={`binance-status-badge ${wa.status}`}>
                        {wa.status === 'verified' && `✓ ${t('wallet.addressVerified')}`}
                        {wa.status === 'pending' && `⏳ ${t('wallet.addressPending')}`}
                        {wa.status === 'rejected' && `✕ ${t('wallet.addressRejected')}`}
                        {wa.status === 'none' && t('wallet.addressRequired')}
                      </span>
                    </div>

                    {/* Adresse enregistrée (pending / verified / rejected) */}
                    {wa.address && (
                      <div className="binance-address-row">
                        <span className="binance-network-chip">TRC20</span>
                        <span className="binance-address-value">{wa.address}</span>
                      </div>
                    )}

                    {/* Vérifiée: VERROUILLÉE définitivement */}
                    {wa.status === 'verified' && (
                      <div className="binance-note locked">
                        🔒 {t('wallet.addressLockedNote')}
                      </div>
                    )}

                    {/* En attente de validation admin */}
                    {wa.status === 'pending' && (
                      <div className="binance-note pending">
                        {t('wallet.addressPendingNote')}
                      </div>
                    )}

                    {/* Rejetée: raison + resoumission */}
                    {wa.status === 'rejected' && (
                      <div className="binance-note rejected">
                        {t('wallet.addressRejectedNote')}: {wa.rejectedReason || '—'}
                      </div>
                    )}

                    {/* Formulaire (première soumission ou resoumission après rejet) */}
                    {(wa.status === 'none' || wa.status === 'rejected') && (
                      <form className="binance-form" onSubmit={handleWithdrawalAddressSubmit}>
                        <MiniGuidePopover
                          variant="banner"
                          label={t('wallet.howToLabel')}
                          steps={[
                            t('wallet.addressGuideStep1'),
                            t('wallet.addressGuideStep2'),
                            t('wallet.addressGuideStep3'),
                            t('wallet.addressGuideStep4'),
                            t('wallet.addressGuideStep5'),
                            t('wallet.addressGuideStep6')
                          ]}
                          previewLabel={t('wallet.depGuidePreview')}
                          previewHideLabel={t('wallet.depGuideHidePreview')}
                          preview={
                            <div className="binance-mock-screen">
                              <div className="binance-mock-title">Déposer des USDT</div>
                              <div className="binance-mock-qr-wrap">
                                <QRCodeSVG
                                  value="TExampleFictiveAddress1234567890AB"
                                  size={90}
                                  bgColor="#0b0e11"
                                  fgColor="#f0b90b"
                                  level="M"
                                  includeMargin={false}
                                />
                              </div>
                              <div className="binance-mock-row">
                                <span className="binance-mock-label">Réseau</span>
                                <span className="binance-mock-value">TRX · Tron (TRC20)</span>
                              </div>
                              <div className="binance-mock-row column">
                                <span className="binance-mock-label">Adresse de dépôt</span>
                                <span className="binance-mock-address">TExampleFictiveAddress1234567890AB</span>
                              </div>
                              <p className="binance-mock-fictive-note">{t('wallet.mockFictiveNote')}</p>
                            </div>
                          }
                        />

                        <div className="binance-form-row">
                          <input
                            type="text"
                            className="binance-address-input"
                            placeholder={t('wallet.addressPlaceholder')}
                            value={withdrawalAddressInput}
                            onChange={(e) => setWithdrawalAddressInput(e.target.value.trim().slice(0, 34))}
                            spellCheck={false}
                            autoComplete="off"
                          />
                        </div>

                        <p className="binance-hint">{t('wallet.addressHint')}</p>
                        {addressMsg.text && (
                          <p className={`binance-msg ${addressMsg.type}`}>{addressMsg.text}</p>
                        )}
                        <button
                          type="submit"
                          className="binance-submit-btn"
                          disabled={addressSubmitting || withdrawalAddressInput.trim().length < 34}
                        >
                          {addressSubmitting ? '...' : t('wallet.addressSubmit')}
                        </button>
                        <p className="binance-warning">⚠️ {t('wallet.addressWarning')}</p>
                      </form>
                    )}

                    {/* Message de succès après soumission (avant refresh) */}
                    {wa.status === 'pending' && addressMsg.type === 'success' && (
                      <p className="binance-msg success">{addressMsg.text}</p>
                    )}
                  </div>
                );
              })()}

              {/* Statut de retrait */}
              <div className="withdrawal-status-card">
                <div className="withdrawal-status-header">
                  <span className="withdrawal-icon">💸</span>
                  <h3>{t('wallet.withdrawalStatus')}</h3>
                </div>

                {withdrawalStatus ? (
                  <div className="withdrawal-status-content">
                    {/* Info du mois */}
                    <div className="withdrawal-month-info">
                      <span className="month-label">{t('wallet.currentMonth')}</span>
                      <span className="month-value">{withdrawalStatus.currentMonth}</span>
                    </div>

                    {/* Peut retirer ? */}
                    {!withdrawalStatus.canWithdraw ? (
                      <div className="withdrawal-blocked">
                        <span className="blocked-icon">🔒</span>
                        <p>{t('wallet.cannotWithdrawYet')}</p>
                        <span className="blocked-date">
                          {t('wallet.availableFrom')}: {new Date(withdrawalStatus.canWithdrawFrom).toLocaleDateString(language === 'en' ? 'en-US' : 'fr-FR')}
                        </span>
                      </div>
                    ) : withdrawalStatus.hasSkippedThisMonth ? (
                      <div className="withdrawal-skipped">
                        <span className="skipped-icon">⏭️</span>
                        <p>{t('wallet.skippedThisMonth')}</p>
                        <span className="skipped-until">
                          {t('wallet.skippedUntil')}: {new Date(withdrawalStatus.skipWithdrawalUntil).toLocaleDateString(language === 'en' ? 'en-US' : 'fr-FR')}
                        </span>
                        <div className="skip-bonus">
                          <span className="bonus-icon">🎁</span>
                          <span>+{withdrawalStatus.extraSuggestionsPerDay} {t('wallet.extraSuggestions')}</span>
                        </div>
                      </div>
                    ) : withdrawalStatus.hasExistingRequest ? (
                      <div className="withdrawal-pending">
                        <span className="pending-icon">⏳</span>
                        <p>{t('wallet.pendingRequest')}</p>
                        <div className="pending-details">
                          <div className="pending-row">
                            <span>{t('wallet.requestedAmount')}</span>
                            <span>{formatCurrency(withdrawalStatus.existingRequest.amount)}</span>
                          </div>
                          <div className="pending-row">
                            <span>{t('wallet.fee')} (5%)</span>
                            <span className="fee">-{formatCurrency(withdrawalStatus.existingRequest.fee)}</span>
                          </div>
                          <div className="pending-row total">
                            <span>{t('wallet.netAmount')}</span>
                            <span className="net">{formatCurrency(withdrawalStatus.existingRequest.netAmount)}</span>
                          </div>
                          <div className="pending-row status">
                            <span>{t('wallet.status')}</span>
                            <span className={`status-badge ${withdrawalStatus.existingRequest.status}`}>
                              {withdrawalStatus.existingRequest.status === 'pending' && t('wallet.statusPending')}
                              {withdrawalStatus.existingRequest.status === 'approved' && t('wallet.statusApproved')}
                              {withdrawalStatus.existingRequest.status === 'paid' && t('wallet.statusPaid')}
                            </span>
                          </div>
                        </div>
                        {withdrawalStatus.existingRequest.status === 'pending' && (
                          <button
                            className="cancel-withdrawal-btn"
                            onClick={() => handleCancelWithdrawal(withdrawalStatus.existingRequest.id)}
                          >
                            {t('wallet.cancelRequest')}
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        {/* Adresse de retrait obligatoire avant de pouvoir demander */}
                        {withdrawalStatus.canRequestNow &&
                          withdrawalStatus.withdrawalAddress?.status !== 'verified' && (
                          <div className="withdrawal-blocked binance-required-notice">
                            <span className="blocked-icon">🏦</span>
                            <p>{t('wallet.addressRequiredNotice')}</p>
                          </div>
                        )}

                        {/* Formulaire de demande (adresse vérifiée requise) */}
                        {withdrawalStatus.canRequestNow &&
                          withdrawalStatus.withdrawalAddress?.status === 'verified' && (
                          <div className="withdrawal-form">
                            <div className="form-group">
                              <label>{t('wallet.withdrawAmount')}</label>
                              <div className="amount-input-wrapper">
                                <span className="currency-symbol">USDT</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={withdrawalAmount}
                                  onChange={(e) => setWithdrawalAmount(e.target.value.replace(/[^\d.,]/g, ''))}
                                  placeholder="0.00"
                                />
                                <button
                                  className="max-btn"
                                  onClick={() => setWithdrawalAmount(withdrawalStatus.balance.toString())}
                                >
                                  MAX
                                </button>
                              </div>
                              <span className="available-balance">
                                {t('wallet.availableBalance')}: {formatCurrency(withdrawalStatus.balance)}
                              </span>
                            </div>

                            {withdrawalAmount && parseAmount(withdrawalAmount) > 0 && (() => {
                              const feeRate = (withdrawalStatus?.feePercent || 5) / 100;
                              const gross = parseAmount(withdrawalAmount);
                              return (
                                <div className="withdrawal-preview">
                                  <div className="preview-row">
                                    <span>{t('wallet.grossAmount')}</span>
                                    <span>{formatCurrency(gross)}</span>
                                  </div>
                                  <div className="preview-row fee">
                                    <span>{t('wallet.fee')} ({withdrawalStatus?.feePercent || 5}%)</span>
                                    <span>-{formatCurrency(gross * feeRate)}</span>
                                  </div>
                                  <div className="preview-row net">
                                    <span>{t('wallet.youWillReceive')}</span>
                                    <span>{formatCurrency(gross * (1 - feeRate))}</span>
                                  </div>
                                </div>
                              );
                            })()}

                            {withdrawalError && (
                              <div className="withdrawal-error">{withdrawalError}</div>
                            )}

                            <button
                              className="withdrawal-submit-btn"
                              onClick={handleWithdrawalRequest}
                              disabled={withdrawalLoading || !withdrawalAmount || parseAmount(withdrawalAmount) <= 0}
                            >
                              {withdrawalLoading ? t('wallet.processing') : t('wallet.requestWithdrawal')}
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* Option de sauter des mois */}
                    {withdrawalStatus.canWithdraw && !withdrawalStatus.hasSkippedThisMonth && !withdrawalStatus.hasExistingRequest && (
                      <div className="skip-withdrawal-section">
                        <div className="skip-divider"><span>{t('wallet.or')}</span></div>
                        <button className="skip-inline-card" onClick={() => setShowSkipModal(true)}>
                          <div className="skip-inline-shimmer"/>
                          <div className="skip-inline-left">
                            <span className="skip-inline-gem">💎</span>
                            <div className="skip-inline-text">
                              <span className="skip-inline-title">{t('wallet.skipForBonus')}</span>
                              <span className="skip-inline-hint">{t('wallet.skipBonusHint')}</span>
                            </div>
                          </div>
                          <span className="skip-inline-arrow">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M5 12h14M12 5l7 7-7 7"/>
                            </svg>
                          </span>
                        </button>
                      </div>
                    )}

                    {/* Bonus actuel */}
                    {withdrawalStatus.extraSuggestionsPerDay > 0 && (
                      <div className="current-bonus">
                        <span className="bonus-icon">✨</span>
                        <span>{t('wallet.currentBonus')}: +{withdrawalStatus.extraSuggestionsPerDay} {t('wallet.suggestionsPerDay')}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="withdrawal-loading">
                    <span className="loading-spinner"></span>
                    <span>{t('wallet.loading')}</span>
                  </div>
                )}
              </div>

              {/* Historique des retraits */}
              <div className="withdrawal-history-card">
                <h3>{t('wallet.withdrawalHistory')}</h3>
                {withdrawalHistory.length > 0 ? (
                  <div className="withdrawal-history-list">
                    {withdrawalHistory.map((w) => (
                      <div key={w.id} className={`withdrawal-history-item ${w.status}`}>
                        <div className="withdrawal-history-icon">
                          {w.status === 'pending' && '⏳'}
                          {w.status === 'approved' && '✅'}
                          {w.status === 'paid' && '💰'}
                          {w.status === 'rejected' && '❌'}
                          {w.status === 'cancelled' && '🚫'}
                        </div>
                        <div className="withdrawal-history-info">
                          <span className="withdrawal-history-amount">{formatCurrency(w.netAmount)}</span>
                          <span className="withdrawal-history-date">
                            {new Date(w.requestedAt).toLocaleDateString(language === 'en' ? 'en-US' : 'fr-FR')}
                          </span>
                        </div>
                        <div className="withdrawal-history-status">
                          <span className={`history-status-badge ${w.status}`}>
                            {w.status === 'pending' && t('wallet.statusPending')}
                            {w.status === 'approved' && t('wallet.statusApproved')}
                            {w.status === 'paid' && t('wallet.statusPaid')}
                            {w.status === 'rejected' && t('wallet.statusRejected')}
                            {w.status === 'cancelled' && t('wallet.statusCancelled')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-withdrawal-history">
                    <span className="empty-icon">📋</span>
                    <p>{t('wallet.noWithdrawalHistory')}</p>
                  </div>
                )}
              </div>

              {/* Modal de skip */}
              {showSkipModal && (
                <div className="skip-modal-overlay" onClick={() => setShowSkipModal(false)}>
                  <div className="skip-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="skip-modal-header">
                      <h3>{t('wallet.skipWithdrawal')}</h3>
                      <button className="close-modal" onClick={() => setShowSkipModal(false)}>×</button>
                    </div>
                    <div className="skip-modal-content">
                      <p>{t('wallet.skipExplanation')}</p>

                      <div className="skip-months-selector">
                        <label>{t('wallet.monthsToSkip')}</label>
                        <div className="months-buttons">
                          {[1, 2, 3, 6, 12].map((m) => (
                            <button
                              key={m}
                              className={`month-btn ${skipMonths === m ? 'active' : ''}`}
                              onClick={() => setSkipMonths(m)}
                            >
                              {m} {t('wallet.months')}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="skip-bonus-preview">
                        <span className="bonus-preview-icon">🎁</span>
                        <span>{t('wallet.youWillGet')}: +{skipMonths} {t('wallet.extraSuggestionsPerDay')}</span>
                      </div>

                      <div className="skip-warning">
                        <span className="warning-icon">⚠️</span>
                        <p>{t('wallet.skipWarning')}</p>
                      </div>

                      {withdrawalError && (
                        <div className="withdrawal-error">{withdrawalError}</div>
                      )}

                      <div className="skip-modal-actions">
                        <button
                          className="skip-cancel-btn"
                          onClick={() => setShowSkipModal(false)}
                        >
                          {t('wallet.cancel')}
                        </button>
                        <button
                          className="skip-confirm-btn"
                          onClick={handleSkipMonths}
                          disabled={withdrawalLoading}
                        >
                          {withdrawalLoading ? t('wallet.processing') : t('wallet.confirm')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal de dépôt — rendu via PORTAL directement dans document.body:
            aucun parent (transform, overflow, stacking context) ne peut le
            piéger, et le scroll interne fonctionne de façon fiable sur iOS. */}
        {savingsModal && createPortal(
          <div className="deposit-modal-overlay" onClick={() => setSavingsModal(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
            <div onClick={(e) => e.stopPropagation()} style={{
              width: '100%', maxWidth: 420, background: 'var(--bg-elevated,#12171e)', borderRadius: 16,
              border: '1px solid rgba(45,212,191,0.3)', padding: 20
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <h3 style={{ margin: 0, fontSize: 17 }}>Retirer — {savingsModal.name}</h3>
                <button onClick={() => setSavingsModal(null)} style={{ background: 'none', border: 'none', color: 'inherit', fontSize: 22, cursor: 'pointer', opacity: 0.6 }}>×</button>
              </div>
              <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 14 }}>
                Solde disponible : <strong style={{ color: '#2dd4bf' }}>{formatCurrency(savingsModal.balance)}</strong> · 0 frais · retrait libre
              </div>

              <div style={{ position: 'relative', marginBottom: 12 }}>
                <input
                  type="text" inputMode="decimal" placeholder="0.00"
                  value={savingsAmount}
                  onChange={(e) => setSavingsAmount(e.target.value.replace(/[^\d.,]/g, ''))}
                  style={{ width: '100%', padding: '12px 90px 12px 12px', borderRadius: 10, fontSize: 16,
                    border: '1px solid var(--border-color,#2a2f36)', background: 'var(--bg,#0d1117)', color: 'inherit' }}
                />
                <button
                  onClick={() => setSavingsAmount(String(savingsModal.balance))}
                  style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', padding: '6px 12px',
                    borderRadius: 8, border: '1px solid rgba(45,212,191,0.4)', background: 'transparent', color: '#2dd4bf', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                  Tout retirer
                </button>
              </div>

              {savingsMsg.text && (
                <p style={{ fontSize: 13, margin: '0 0 12px', color: savingsMsg.type === 'success' ? '#22c55e' : '#ef4444' }}>
                  {savingsMsg.text}
                </p>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setSavingsModal(null)}
                  style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid var(--border-color,#2a2f36)', background: 'transparent', color: 'inherit', cursor: 'pointer' }}>
                  Annuler
                </button>
                <button onClick={handleSavingsWithdraw} disabled={savingsSubmitting}
                  style={{ flex: 2, padding: '12px', borderRadius: 10, border: 'none', background: '#2dd4bf', color: '#04211d', fontWeight: 700, cursor: savingsSubmitting ? 'wait' : 'pointer' }}>
                  {savingsSubmitting ? 'Envoi…' : 'Confirmer le retrait'}
                </button>
              </div>
              <p style={{ fontSize: 11, opacity: 0.55, margin: '12px 0 0', textAlign: 'center' }}>
                Nécessite une adresse TRC20 et un KYC vérifiés. La demande est validée par l'administrateur.
              </p>
            </div>
          </div>,
          document.body
        )}

        {showDepositModal && createPortal(
          <div className="deposit-modal-overlay" onClick={() => { setShowDepositModal(false); clearInterval(depositTimerRef.current); }}>
            <div className="deposit-modal" onClick={(e) => e.stopPropagation()}>

              {/* Header */}
              <div className="deposit-modal-header">
                <div className="deposit-modal-title">
                  <div className="deposit-modal-icon-wrap">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0b0e11" strokeWidth="2.5">
                      <path d="M12 5v14M5 12l7-7 7 7"/>
                    </svg>
                  </div>
                  <span>{t('wallet.depositModalTitle')}</span>
                </div>
                <button className="deposit-modal-close" onClick={() => { setShowDepositModal(false); clearInterval(depositTimerRef.current); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              <div className="deposit-modal-body">
                {/* Sélecteur de méthode de dépôt */}
                <div className="deposit-method-tabs">
                  <button
                    type="button"
                    className={`deposit-method-tab ${depositMethod === 'txid' ? 'active' : ''}`}
                    onClick={() => setDepositMethod('txid')}
                  >
                    {t('wallet.depMethodTxid')}
                  </button>
                  <button
                    type="button"
                    className={`deposit-method-tab ${depositMethod === 'code' ? 'active' : ''}`}
                    onClick={() => setDepositMethod('code')}
                  >
                    {t('wallet.depMethodCode')}
                  </button>
                </div>

                {depositMethod === 'txid' ? (
                  /* ── MÉTHODE : virement réel (réseau TRC20, TXID) ── */
                  <div className="deposit-txid-section">
                    <div className="dep-address-card">
                      <div className="dep-address-header">
                        <span className="binance-network-chip">TRC20</span>
                        <span className="dep-address-label-text">{t('wallet.depAddressLabel')}</span>
                        <MiniGuidePopover
                        variant="inline"
                        label={t('wallet.howToLabel')}
                        steps={[
                          t('wallet.depGuideStep1'),
                          t('wallet.depGuideStep2'),
                          t('wallet.depGuideStep3'),
                          t('wallet.depGuideStep4'),
                          t('wallet.depGuideStep5'),
                          t('wallet.depGuideStep6')
                        ]}
                        previewLabel={t('wallet.depGuidePreview')}
                        previewHideLabel={t('wallet.depGuideHidePreview')}
                        preview={
                          <>
                            <div className="binance-mock-screen">
                              <div className="binance-mock-title">Déposer des USDT</div>
                              <div className="binance-mock-qr-wrap">
                                <QRCodeSVG
                                  value={depositAddress || 'TExampleFictiveAddress1234567890AB'}
                                  size={90}
                                  bgColor="#0b0e11"
                                  fgColor="#f0b90b"
                                  level="M"
                                  includeMargin={false}
                                />
                              </div>
                              <div className="binance-mock-row">
                                <span className="binance-mock-label">Réseau</span>
                                <span className="binance-mock-value">TRX · Tron (TRC20)</span>
                              </div>
                              <div className="binance-mock-row column">
                                <span className="binance-mock-label">Adresse de dépôt</span>
                                <span className="binance-mock-address">{depositAddress || '···'}</span>
                              </div>
                            </div>
                            <div className="binance-mock-screen">
                              <div className="binance-mock-title">Envoyer USDT</div>
                              <div className="binance-mock-field">
                                <span className="binance-mock-label">Adresse</span>
                                <div className="binance-mock-input">
                                  <span className="binance-mock-address-small">{depositAddress || '···'}</span>
                                  <button type="button" className="binance-mock-copy" onClick={handleCopyAddress}>⧉</button>
                                </div>
                              </div>
                              <div className="binance-mock-field">
                                <span className="binance-mock-label">Réseau</span>
                                <div className="binance-mock-input muted">TRX · Tron (TRC20)</div>
                              </div>
                              <div className="binance-mock-field">
                                <span className="binance-mock-label">Montant retiré</span>
                                <div className="binance-mock-input muted">Minimum 0</div>
                              </div>
                              <div className="binance-mock-balance">
                                Disponible : <span className="binance-mock-redacted">▓▓▓▓▓▓▓</span>
                              </div>
                              <div className="binance-mock-cta">Retrait</div>
                            </div>
                          </>
                        }
                      />
                      </div>

                      {depositAddress ? (
                        <>
                          <div className="dep-address-box">
                            <code className="dep-token-text">{depositAddress}</code>
                          </div>
                          <button type="button" className={`dep-copy-btn ${addressCopied ? 'copied' : ''}`} onClick={handleCopyAddress}>
                            {addressCopied ? (
                              <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                                {t('wallet.depCopied')}
                              </>
                            ) : (
                              <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                </svg>
                                {t('wallet.depCopy')}
                              </>
                            )}
                          </button>
                        </>
                      ) : (
                        <p className="dep-address-unavailable">{t('wallet.depAddressUnavailable')}</p>
                      )}
                      <p className="binance-warning">⚠️ {t('wallet.depNetworkWarning')}</p>
                    </div>

                    <form className="binance-form dep-txid-form" onSubmit={handleSubmitTxid}>
                      <label className="dep-token-label">{t('wallet.depTxidLabel')}</label>
                      <input
                        type="text"
                        className="binance-address-input"
                        placeholder={t('wallet.depTxidPlaceholder')}
                        value={txidInput}
                        onChange={(e) => setTxidInput(e.target.value)}
                        spellCheck={false}
                        autoComplete="off"
                      />
                      <label className="dep-token-label">{t('wallet.depTxidAmountLabel')}</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="binance-address-input"
                        placeholder="0.00"
                        value={txidAmountInput}
                        onChange={(e) => {
                          // Accepte point ET virgule comme séparateur décimal,
                          // quelle que soit la locale du navigateur.
                          const v = e.target.value.replace(/[^\d.,]/g, '');
                          setTxidAmountInput(v);
                        }}
                      />
                      {txidMsg.text && <p className={`binance-msg ${txidMsg.type}`}>{txidMsg.text}</p>}
                      <button
                        type="submit"
                        className="binance-submit-btn"
                        disabled={txidSubmitting || !txidInput.trim() || !txidAmountInput || !(parseAmount(txidAmountInput) > 0)}
                      >
                        {txidSubmitting ? '...' : t('wallet.depTxidSubmit')}
                      </button>
                    </form>

                    {depositHistory.length > 0 && (
                      <div className="dep-history-list">
                        <span className="dep-history-title">{t('wallet.depHistoryTitle')}</span>
                        {depositHistory.map(d => (
                          <div key={d.id} className={`dep-history-item ${d.status}`}>
                            <span className="dep-history-txid">{d.txid.slice(0, 10)}…</span>
                            <span className="dep-history-amount">{formatCurrency(d.amount)}</span>
                            <span className={`history-status-badge ${d.status}`}>
                              {d.status === 'pending' && t('wallet.statusPending')}
                              {d.status === 'approved' && t('wallet.statusApproved')}
                              {d.status === 'rejected' && t('wallet.statusRejected')}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : !depositToken ? (
                  /* ── ÉTAT : pas encore de token ── */
                  <div className="deposit-idle-state">
                    <div className="deposit-shield-anim">
                      <div className="dep-ring dep-ring-1"/>
                      <div className="dep-ring dep-ring-2"/>
                      <div className="dep-shield-core">
                        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#f0b90b" strokeWidth="1.8">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                        </svg>
                      </div>
                    </div>

                    <p className="deposit-idle-hint">
                      {t('wallet.depositIdleHint')}
                    </p>

                    <div className="deposit-steps-row">
                      <div className="dep-step"><span className="dep-step-n">1</span><span>{t('wallet.depStepGenerate')}</span></div>
                      <div className="dep-step-arrow">›</div>
                      <div className="dep-step"><span className="dep-step-n">2</span><span>{t('wallet.depStepSend')}</span></div>
                      <div className="dep-step-arrow">›</div>
                      <div className="dep-step"><span className="dep-step-n">3</span><span>{t('wallet.depStepReceive')}</span></div>
                    </div>

                    {depositError && <div className="deposit-error">{depositError}</div>}

                    <button className="deposit-generate-btn" onClick={handleGenerateDepositToken} disabled={depositLoading}>
                      {depositLoading
                        ? <><span className="deposit-spinner"/> {t('wallet.depositGenerating')}</>
                        : <>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                            </svg>
                            {t('wallet.depositGenerateBtn')}
                          </>
                      }
                    </button>
                  </div>
                ) : (
                  /* ── ÉTAT : token généré ── */
                  <div className="deposit-token-state">
                    {/* Barre de compte à rebours */}
                    <div className="dep-countdown-wrap">
                      <div className="dep-countdown-row">
                        <span className="dep-countdown-label">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                          </svg>
                          {t('wallet.depCodeActive')}
                        </span>
                        <span className={`dep-countdown-num ${tokenTimeLeft <= 10 ? 'red' : tokenTimeLeft <= 20 ? 'orange' : ''}`}>
                          {tokenTimeLeft}s
                        </span>
                      </div>
                      <div className="dep-progress-track">
                        <div
                          className={`dep-progress-fill ${tokenTimeLeft <= 10 ? 'red' : tokenTimeLeft <= 20 ? 'orange' : ''}`}
                          style={{ width: `${(tokenTimeLeft / 30) * 100}%` }}
                        />
                      </div>
                    </div>

                    {/* Token */}
                    <div className="dep-token-label">{t('wallet.depTokenLabel')}</div>
                    <div className="dep-token-box">
                      <code className="dep-token-text">{depositToken}</code>
                    </div>

                    {/* QR Code */}
                    <div className="dep-qr-wrap">
                      <QRCodeSVG
                        value={depositToken}
                        size={120}
                        bgColor="#1a1d21"
                        fgColor="#f0b90b"
                        level="M"
                        includeMargin={false}
                      />
                      <span className="dep-qr-hint">{t('wallet.depScanQr')}</span>
                    </div>

                    {/* Actions */}
                    <button className={`dep-copy-btn ${tokenCopied ? 'copied' : ''}`} onClick={handleCopyToken}>
                      {tokenCopied ? (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                          {t('wallet.depCopied')}
                        </>
                      ) : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                          </svg>
                          {t('wallet.depCopy')}
                        </>
                      )}
                    </button>

                    <div className="dep-token-footer">
                      <span className="dep-token-warning">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                        {t('wallet.depNeverShare')}
                      </span>
                      <button className="dep-regen-btn" onClick={handleGenerateDepositToken} disabled={depositLoading}>
                        ↺ {t('wallet.depNewCode')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Footer Info */}
        <div className="wallet-footer">
          <div className="footer-item">
            <span className="footer-icon">🔒</span>
            <span className="footer-text">{t('wallet.securedFunds')}</span>
          </div>
          <div className="footer-item">
            <span className="footer-icon">💰</span>
            <span className="footer-text">{t('wallet.smartTrading')}</span>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Wallet;
